import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, StatCard, Button, Badge } from '@/backoffice/components/ui';
import { fetchAdminDashboard } from '@/domains/admin/services/admin.service';
import { formatCurrency } from '@/backoffice/utils';
import { Users, FileText, MapPin, AlertCircle, Package, Gavel, Loader2 } from 'lucide-react';

export function AdminDashboard() {
  const [dashboard, setDashboard] = useState<Awaited<ReturnType<typeof fetchAdminDashboard>> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAdminDashboard()
      .then(setDashboard)
      .catch(() => setDashboard(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !dashboard) {
    return (
      <DashboardLayout userType="admin" title="Tableau de bord">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  const stats = dashboard.stats;

  return (
    <DashboardLayout userType="admin" title="Tableau de bord">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard title="Utilisateurs" value={stats.total_users} icon={<Users className="w-6 h-6 text-indigo-600" />} iconBg="bg-indigo-50" />
        <StatCard title="Commandes actives" value={stats.active_orders} icon={<Package className="w-6 h-6 text-blue-600" />} iconBg="bg-blue-50" />
        <StatCard title="Revenus du mois" value={formatCurrency(stats.month_revenue)} icon={<Package className="w-6 h-6 text-emerald-600" />} iconBg="bg-emerald-50" />
        <StatCard title="Satisfaction" value={`${stats.average_satisfaction}/5`} icon={<Users className="w-6 h-6 text-amber-600" />} iconBg="bg-amber-50" />
      </div>

      {dashboard.notifications.length > 0 && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {dashboard.notifications.map((n) => (
            <Card key={n.id} className="bg-amber-50 border-amber-200">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <div className="flex-1">
                  <p className="font-medium text-amber-900">{n.titre}</p>
                  <p className="text-sm text-amber-700">{n.message}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <Card padding="none">
          <div className="p-4 border-b flex justify-between items-center">
            <h2 className="font-semibold">Utilisateurs récents</h2>
            <Link to="/dashboard/admin/utilisateurs" className="text-sm text-indigo-600">Voir tout</Link>
          </div>
          <div className="divide-y">
            {dashboard.recent_users.map((user) => (
              <div key={user.id} className="p-4 flex justify-between">
                <div>
                  <p className="font-medium">{user.name}</p>
                  <p className="text-sm text-gray-500">{user.type?.replace('_', ' ')}</p>
                </div>
                <Badge variant={user.status === 'active' ? 'success' : user.status === 'pending' ? 'warning' : 'default'}>
                  {user.status}
                </Badge>
              </div>
            ))}
          </div>
        </Card>

        <Card padding="none">
          <div className="p-4 border-b flex justify-between items-center">
            <h2 className="font-semibold">Documents en attente</h2>
            <Link to="/dashboard/admin/documents" className="text-sm text-indigo-600">Voir tout</Link>
          </div>
          {dashboard.pending_documents.length === 0 ? (
            <p className="p-8 text-center text-gray-500">Aucun document en attente</p>
          ) : (
            <div className="divide-y">
              {dashboard.pending_documents.map((doc) => (
                <div key={doc.id} className="p-4 flex justify-between">
                  <div>
                    <p className="font-medium">{doc.category}</p>
                    <p className="text-sm text-gray-500">{doc.user_name}</p>
                  </div>
                  <Badge variant="warning">En attente</Badge>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card>
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <MapPin className="w-5 h-5 text-indigo-600" />
          Utilisateurs actifs par zone
        </h3>
        <div className="grid md:grid-cols-3 gap-3">
          {dashboard.users_by_zone.slice(0, 6).map((zone) => (
            <div key={zone.zone_id} className="p-3 bg-gray-50 rounded-lg">
              <p className="font-medium">{zone.zone_name}</p>
              <p className="text-sm text-gray-600">{zone.active_users} actifs / {zone.users_count} total</p>
            </div>
          ))}
        </div>
      </Card>
    </DashboardLayout>
  );
}
